import { HttpContextToken } from '@angular/common/http';
import { NetworkConstant } from './network-constant';

/**
 * Set on a telemetry HTTP request so the auth and response interceptors apply
 * telemetry-appropriate behaviour: analytics API key, access token only, 401
 * pauses sync rather than logging out.
 */
export const TELEMETRY_REQUEST = new HttpContextToken<boolean>(() => false);

/** Classifies a request for header selection — mirrors Consumer's RequestInterceptor `ApiType`. */
export type ApiType = 'SSO' | 'CDN' | 'GuestToken' | 'AccessToken' | 'None';

/** Carries the resolved {@link ApiType} from the auth interceptor to the response interceptor via HttpContext. */
export const API_TYPE = new HttpContextToken<ApiType>(() => 'None');

const URL_PATTERNS: Record<Exclude<ApiType, 'AccessToken' | 'None'>, string[]> = {
  SSO: [NetworkConstant.GUEST_AUTH_TOKEN_V2, NetworkConstant.GUEST_AUTH_TOKEN_V3],
  CDN: [NetworkConstant.VDI_TOUR_CONFIG, NetworkConstant.APP_CONFIG_V2, NetworkConstant.FEATURE_TOGGLE_CONFIG_CDN],
  GuestToken: [
    NetworkConstant.USER_LOGIN,
    NetworkConstant.GUEST_USER_ELIGIBILITY,
    NetworkConstant.MOBILE_SUBSCRIBERS,
    NetworkConstant.OTP_SEND,
    NetworkConstant.OTP_VERIFY,
    NetworkConstant.PWA_LOGIN,
    NetworkConstant.LOGIN_2FA_SEND,
    NetworkConstant.LOGIN_2FA_VERIFY,
    NetworkConstant.FORGOT_PASSWORD,
    NetworkConstant.FORGOT_OTP_VERIFY,
    NetworkConstant.REGISTER_USERNAME,
    NetworkConstant.VALIDATE_REGISTRATION_OTP,
    NetworkConstant.REFRESH_RMN,
    NetworkConstant.FORGOT_PASSWORD_SEND_OTP,
    NetworkConstant.FORGOT_PASSWORD_VALIDATE,
    NetworkConstant.FORGOT_PASSWORD_VERIFY_OTP,
    NetworkConstant.GET_TRIAL_PLANS,
    NetworkConstant.TRIAL_PAYMENT_QR,
    NetworkConstant.TRIAL_PAYMENT_STATUS,
    NetworkConstant.ONBOARDING_VOUCHER_REDEEM,
  ],
};

/** Endpoints Consumer retries on 408/5xx with exponential backoff (network/ResponseInterceptor.ts `RETRY_ENDPOINTS`). */
export const RETRY_ENDPOINTS: string[] = [
  NetworkConstant.GUEST_AUTH_TOKEN_V2,
  NetworkConstant.GUEST_AUTH_TOKEN_V3,
  NetworkConstant.USER_LOGIN,
  NetworkConstant.PROFILE_DETAILS,
];

/** The two encrypted API-key combos, CMS-sourced via AppConfigStore and cached in LocalStorageManagerService. */
export interface EncryptedApiKeys {
  clientApiKey: string;
  analyticsAPIKey: string;
}

/**
 * Picks the *encrypted* API-key combo for a request, to be handed to
 * `ApiKeyCacheService.getDecryptedKey()` for the `x-api-key` header.
 *
 * BFF functional logs (`api/log/user`) and VDI telemetry (`api/v2/telemetry/events`)
 * are authenticated with their own `analyticsAPIKey`; every other endpoint uses the general `clientApiKey`.
 * Returns the encrypted value — decryption stays the caller's job so both keys
 * share the same in-memory cache (keyed by the encrypted combo).
 */
export function resolveEncryptedApiKey(url: string, keys: EncryptedApiKeys): string {
  if (url.includes(NetworkConstant.USER_LOG)) return keys.analyticsAPIKey;
  if (url.includes(NetworkConstant.TELEMETRY_EVENTS)) return keys.analyticsAPIKey;
  return keys.clientApiKey;
}


/** Resolves the {@link ApiType} for a request URL — same precedence order as Consumer's RequestInterceptor. */
export function resolveApiType(url: string, hasAccessToken: boolean): ApiType {
  if (URL_PATTERNS.SSO.some((pattern) => url.includes(pattern))) return 'SSO';
  if (URL_PATTERNS.CDN.some((pattern) => url.includes(pattern))) return 'CDN';
  // Functional logs: prefer access token when logged in; guest token for pre-auth (OTP / leads).
  if (url.includes(NetworkConstant.USER_LOG)) {
    return hasAccessToken ? 'AccessToken' : 'GuestToken';
  }
  if (URL_PATTERNS.GuestToken.some((pattern) => url.includes(pattern))) return 'GuestToken';
  if (hasAccessToken) return 'AccessToken';
  return 'None';
}
